import type { InjectionProtocol, ProtocolDoseUnit } from "./types";

/**
 * Her recorded dose against the label's, decided before any step can say it.
 *
 * The walk fills `{dose}` from her medication record, and the record is hers:
 * she typed a number and picked a unit from a list of eleven. The coach used to
 * repeat the result verbatim, so a patient who chose `mg` for Elonva was told,
 * on the step where she checks her syringe, that it holds "150 mg" of a drug
 * measured in micrograms. Nothing was wrong with the number and everything was
 * wrong with the sentence, and no code could tell, because no protocol said
 * what its unit was.
 *
 * Now every protocol declares one (`ProtocolDose`), and this file is the only
 * place her record meets it. The rule is Anaïs's from the Menopur vial sizes:
 * **a mismatch counts as nothing recorded.** The coach never converts, never
 * guesses, and never says a dose it cannot place — it asks her what the box
 * says, or it says nothing.
 */

/** Her record's dose, read as a number and a unit. */
export type RecordedDose = {
  value: number;
  /** Canonical, or null when the word is not one a label uses. */
  unit: ProtocolDoseUnit | null;
  /** As she wrote it, for the sentence that quotes her record back to her. */
  text: string;
};

export type DoseDecision =
  /** Nothing recorded, or nothing readable as a dose: the steps say nothing. */
  | { kind: "none" }
  /**
   * Her record agrees with the label, or the protocol declares no unit to hold
   * it to; this is what the steps may say.
   */
  | { kind: "matches"; dose: string }
  /**
   * Her record and the label disagree — on the unit, or on a number no
   * presentation of this product exists in. The steps may not repeat it.
   */
  | {
      kind: "mismatch";
      reason: "unit" | "strength";
      recorded: RecordedDose;
      labelUnit: ProtocolDoseUnit;
      /** Formatted, in the label's unit, for the question to offer. May be empty. */
      strengths: readonly string[];
    };

/**
 * The words a record may carry for each unit, both languages, both scripts.
 *
 * `units` is folded into IU: it is the add form's own option and, on an
 * injectable measured in international units, it means those. `ml` is
 * deliberately absent — a volume is not a dose, and a record in millilitres is
 * treated as an unrecognised unit rather than matched to one of these.
 */
const UNIT_WORDS: Record<ProtocolDoseUnit, readonly string[]> = {
  mcg: [
    "mcg",
    "µg",
    "μg",
    "ug",
    "microgram",
    "micrograms",
    "microgramme",
    "microgrammes",
  ],
  mg: ["mg", "milligram", "milligrams", "milligramme", "milligrammes"],
  IU: ["iu", "ui", "units", "unit", "unité", "unités", "unite", "unites"],
};

export function normalizeDoseUnit(raw: string): ProtocolDoseUnit | null {
  const word = raw.trim().toLowerCase();
  if (!word) return null;
  for (const [unit, words] of Object.entries(UNIT_WORDS) as [
    ProtocolDoseUnit,
    readonly string[],
  ][]) {
    if (words.includes(word)) return unit;
  }
  return null;
}

/**
 * "150 mcg", "0,25 mg", "10 000 IU" — a number, then a unit word, and nothing
 * else. Anything looser is not read: a dose the coach is about to repeat has
 * to be unambiguous, and "1 vial" or "2 tablets" is not a dose of anything.
 */
const RECORDED_DOSE = /^(\d[\d\s  ]*(?:[.,]\d+)?)\s*([^\d\s]+)$/u;

export function parseRecordedDose(
  text: string | null | undefined,
): RecordedDose | null {
  const trimmed = text?.trim() ?? "";
  if (!trimmed) return null;
  const match = RECORDED_DOSE.exec(trimmed);
  if (!match) return null;
  const value = Number(match[1]!.replaceAll(/[\s  ]/gu, "").replace(",", "."));
  if (!Number.isFinite(value) || value <= 0) return null;
  return { value, unit: normalizeDoseUnit(match[2]!), text: trimmed };
}

/** `0.25 mg`, `150 mcg`, `10000 IU` — the number as JavaScript prints it. */
export function formatDose(value: number, unit: ProtocolDoseUnit): string {
  return `${value} ${unit}`;
}

export function doseDecision(
  protocol: InjectionProtocol,
  recordedText: string | null | undefined,
): DoseDecision {
  // Nothing to check her record against: it is said as it stands.
  if (!protocol.dose) {
    const text = recordedText?.trim();
    return text ? { kind: "matches", dose: text } : { kind: "none" };
  }

  const recorded = parseRecordedDose(recordedText);
  if (!recorded) return { kind: "none" };

  const { unit: labelUnit, alsoUnits = [], strengths = [] } = protocol.dose;
  const formattedStrengths = strengths.map((value) =>
    formatDose(value, labelUnit),
  );

  // A box that prints two scales is matched by either; the dose is then said
  // in the unit she recorded, never converted to the other.
  const accepted: readonly ProtocolDoseUnit[] = [labelUnit, ...alsoUnits];
  if (!recorded.unit || !accepted.includes(recorded.unit)) {
    return {
      kind: "mismatch",
      reason: "unit",
      recorded,
      labelUnit,
      strengths: formattedStrengths,
    };
  }
  if (strengths.length > 0 && !strengths.includes(recorded.value)) {
    return {
      kind: "mismatch",
      reason: "strength",
      recorded,
      labelUnit,
      strengths: formattedStrengths,
    };
  }
  return { kind: "matches", dose: formatDose(recorded.value, recorded.unit) };
}
